import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, LogOut, Menu, Moon, Sun } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_NAV, MODULES } from "@/lib/nav";
import { ROLE_LABEL } from "@/lib/format";
import { SyncIndicator } from "@/components/SyncIndicator";

function NavItem({ item, onNavigate, mini }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={onNavigate}
      title={mini ? item.label : undefined}
      data-testid={`nav-${item.id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${mini ? "justify-center" : ""} ${isActive ? "bg-white/12 text-white font-semibold shadow-[inset_3px_0_0_#F5A623]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
    >
      <Icon className="h-4 w-4 shrink-0" />{!mini && item.label}
    </NavLink>
  );
}

const HARI = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BLN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function Clock({ mini }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: mini ? undefined : "2-digit" });
  return (
    <div data-testid="sidebar-clock" className={`border-t border-white/10 py-4 text-slate-300 ${mini ? "px-1 text-center text-[10px]" : "px-5"}`}>
      <p className={`font-num font-semibold text-white ${mini ? "" : "text-lg"}`}>{time}</p>
      {!mini && <p className="text-[11px]">{HARI[now.getDay()]}, {now.getDate()} {BLN[now.getMonth()]} {now.getFullYear()}</p>}
    </div>
  );
}

function SideNav({ onNavigate, mini, onToggle }) {
  const { user } = useAuth();
  const can = (id) => id === "ringkasan" || user.role === "admin" || (user.menus || []).includes(id);
  const admin = ADMIN_NAV.filter((n) => n.roles.includes(user.role) && (n.id !== "laporan" || can("laporan")));
  const modules = MODULES.filter((m) => can(m.id));
  return (
    <div className="grid-bg flex h-full flex-col bg-[#0A3967] text-white">
      <div className={mini ? "px-2 py-5" : "px-4 py-5"}>
        {!mini && <>
          <div className="rounded-lg bg-white px-3 py-2"><img data-testid="sidebar-logo" src="/pln-logo.png" alt="PLN Electricity Services" className="h-8 w-auto" /></div>
          <p className="mt-2 px-1 font-heading text-sm font-bold">Kinerja Distribusi</p>
          <p className="px-1 text-[11px] text-slate-300">Dashboard Monitoring</p>
        </>}
        {onToggle && (
          <button data-testid="sidebar-toggle-button" onClick={onToggle} aria-label={mini ? "Perbesar sidebar" : "Perkecil sidebar"} title={mini ? "Perbesar sidebar" : "Perkecil sidebar"}
            className={`flex items-center gap-2 rounded-md text-xs text-slate-300 hover:bg-white/10 hover:text-white ${mini ? "mx-auto p-2" : "mt-3 px-2 py-1"}`}>
            {mini ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" />Perkecil</>}
          </button>
        )}
      </div>
      <nav className={`flex-1 space-y-1 overflow-y-auto pb-4 ${mini ? "px-2" : "px-3"}`}>
        {!mini && <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Modul Kinerja</p>}
        {modules.map((m) => <NavItem key={m.id} item={m} onNavigate={onNavigate} mini={mini} />)}
        {admin.length > 0 && (mini ? <div className="mx-2 my-3 border-t border-white/10" /> : <p className="px-3 pb-1 pt-5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Administrasi</p>)}
        {admin.map((m) => <NavItem key={m.id} item={m} onNavigate={onNavigate} mini={mini} />)}
      </nav>
      <Clock mini={mini} />
    </div>
  );
}

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useTheme();
  const [mini, setMini] = useState(() => localStorage.getItem("sidebar") === "mini");
  const toggleMini = () => {
    localStorage.setItem("sidebar", mini ? "full" : "mini");
    setMini(!mini);
  };
  return (
    <div className="min-h-screen bg-background">
      <aside data-testid="desktop-sidebar" className={`fixed inset-y-0 left-0 z-40 hidden transition-[width] duration-200 lg:block ${mini ? "w-16" : "w-64"}`}>
        <SideNav mini={mini} onToggle={toggleMini} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 p-0">
          <SheetTitle className="sr-only">Navigasi</SheetTitle>
          <SheetDescription className="sr-only">Menu modul kinerja dan administrasi</SheetDescription>
          <SideNav onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className={`transition-[padding] duration-200 ${mini ? "lg:pl-16" : "lg:pl-64"}`}>
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between gap-3 border-b bg-background/95 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></Button>
          <div className="flex-1" />
          <SyncIndicator />
          <Button data-testid="theme-toggle-button" variant="ghost" size="icon" aria-label={dark ? "Mode terang" : "Mode gelap"} title={dark ? "Mode terang" : "Mode gelap"} onClick={() => setDark(!dark)}>
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-sm hover:bg-muted">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#0A3967] text-xs font-bold text-white">{(user.name || user.username)[0].toUpperCase()}</span>
                <span className="hidden sm:inline font-medium">{ROLE_LABEL[user.role]}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>
                <p className="font-semibold">{user.name}</p>
                <p className="text-xs font-normal text-muted-foreground">@{user.username}</p>
                <p data-testid="user-role-label" className="mt-1 text-xs font-normal">Peran: {ROLE_LABEL[user.role]}{user.units?.length ? ` · ${user.units.join(", ")}` : ""}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout}><LogOut className="mr-2 h-4 w-4" />Keluar</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main><Outlet /></main>
      </div>
    </div>
  );
}
